import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { UserContextService } from '../services/user-context.service';
import { environment } from '../../../environments/environment';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const userContext = inject(UserContextService);
  const router = inject(Router);
  const notificationService = inject(NotificationService);

  const token = authService.token();
  const companyId = userContext.selectedCompanyId();
  const isApiUrl = req.url.startsWith(environment.apiUrl);

  let headersToSet: Record<string, string> = {};
  if (token) {
    headersToSet['Authorization'] = `Bearer ${token}`;
  }
  if (companyId) {
    headersToSet['X-Company-Id'] = companyId;
  }

  let authReq = req;
  if (isApiUrl && Object.keys(headersToSet).length > 0) {
    authReq = req.clone({
      setHeaders: headersToSet,
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 403) {
        const msg = error.error?.message || error.error?.detail || 'Você não possui permissão para realizar esta operação nesta empresa.';
        notificationService.showToast('danger', msg, 'Acesso Negado (403)');
      } else if (error.status === 401 && isApiUrl && !req.headers.has('X-Skip-Refresh')) {
        return from(authService.getFreshToken()).pipe(
          switchMap((newToken) => {
            if (newToken) {
              const retryHeaders: Record<string, string> = {
                Authorization: `Bearer ${newToken}`,
                'X-Skip-Refresh': 'true',
              };
              if (companyId) {
                retryHeaders['X-Company-Id'] = companyId;
              }
              const retriedReq = req.clone({
                setHeaders: retryHeaders,
              });
              return next(retriedReq).pipe(
                catchError((retryErr: HttpErrorResponse) => {
                  if (retryErr.status === 401) {
                    handleSessionExpired(authService, router, notificationService);
                  }
                  return throwError(() => retryErr);
                })
              );
            } else {
              handleSessionExpired(authService, router, notificationService);
              return throwError(() => error);
            }
          }),
          catchError((refreshErr) => {
            handleSessionExpired(authService, router, notificationService);
            return throwError(() => refreshErr);
          })
        );
      }

      return throwError(() => error);
    })
  );
};

function handleSessionExpired(
  authService: AuthService,
  router: Router,
  notificationService: NotificationService
) {
  authService.logout();
  const currentUrl = router.url;
  const returnUrl = currentUrl && !currentUrl.includes('/auth/') ? currentUrl : '/dashboard';
  router.navigate(['/auth/login'], { queryParams: { returnUrl } });
  notificationService.showToast(
    'warning',
    'Sua sessão expirou. Por favor, faça login novamente.',
    'Sessão Expirada'
  );
}
